import { useState, useEffect } from 'react';
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';
import { __ } from '@wordpress/i18n';
import {
	Button,
	Card,
	CardHeader,
	CardBody,
	CardDivider,
	Modal,
	Spinner,
} from '@wordpress/components';
import TableRow from '../../components/TableRow';
import Table from '../../components/Table';
import ShortcutsGrid from '../../components/ShortcutsGrid';
import Loader from '../../components/Loader';

const SiteInformation = () => {
	const [ siteInfo, setSiteInfo ] = useState( null );
	const [ isLoading, setIsLoading ] = useState( true );
	const [ isModalOpen, setIsModalOpen ] = useState( false );
	const [ clearingCache, setClearingCache ] = useState( false );
	const baseURL = boilpressApi.baseURL;

	const copyToClipboard = ( e ) => {
		navigator.clipboard.writeText( JSON.stringify( siteInfo, null, 2 ) );
		e.target.innerHTML = __( 'Copied!', 'boilpress' );
		setTimeout(
			() =>
				( e.target.innerHTML = __( 'Copy to Clipboard', 'boilpress' ) ),
			3000
		);
	};

	useEffect( () => {
		const fetchSiteInfo = async () => {
			try {
				const response = await apiFetch( {
					path: addQueryArgs( '/boilpress/v1/site-info' ),
					headers: { 'X-WP-Nonce': boilpressApi.nonce },
				} );
				if ( response && typeof response === 'object' ) {
					setSiteInfo( response );
				} else {
					throw new Error( 'Unexpected response format' );
				}
			} catch ( error ) {
				console.error( 'Failed to fetch site info:', error.message );
			} finally {
				setIsLoading( false );
			}
		};
		fetchSiteInfo();
	}, [] );

	const clearCache = async () => {
		setClearingCache( true );
		try {
			const response = await apiFetch( {
				path: '/boilpress/v1/clear-cache',
				method: 'POST',
				headers: { 'X-WP-Nonce': boilpressApi.nonce },
			} );
			if ( response.status === 'Cache cleared successfully' ) {
				console.log( 'Cache cleared successfully' );
			}
		} catch ( error ) {
			console.error( 'Failed to clear cache:', error.message );
		} finally {
			setClearingCache( false );
			setIsModalOpen( false );
		}
	};

	return (
		<div
			style={ {
				maxWidth: '820px',
			} }
		>
			{ isLoading ? (
				<Loader />
			) : (
				siteInfo && (
					<>
						<Card>
							<CardHeader>
								{ __( 'Shortcuts', 'boilpress' ) }
							</CardHeader>
							<CardBody>
								<ShortcutsGrid />
							</CardBody>
							<CardDivider />
							<CardHeader>
								{ __( 'Site Information', 'boilpress' ) }
							</CardHeader>
							<CardBody>
								<Table>
									<tbody>
										<TableRow
											label={ __(
												'WordPress Version:',
												'boilpress'
											) }
											value={ siteInfo.wordpress_version }
										/>
										<TableRow
											label={ __(
												'PHP Version:',
												'boilpress'
											) }
											value={ siteInfo.php_version }
										/>
										<TableRow
											label={ __(
												'Site URL:',
												'boilpress'
											) }
											value={
												<a
													href={ siteInfo.site_url }
													target="_blank"
													rel="noopener noreferrer"
												>
													{ siteInfo.site_url }
												</a>
											}
										/>
										<TableRow
											label={ __(
												'Admin Email:',
												'boilpress'
											) }
											value={ siteInfo.admin_email }
										/>
										<TableRow
											label={ __(
												'Language:',
												'boilpress'
											) }
											value={ siteInfo.language }
										/>
										<TableRow
											label={ __(
												'Active Theme:',
												'boilpress'
											) }
											value={ siteInfo.active_theme }
										/>
										<TableRow
											label={ __(
												'WP Directory:',
												'boilpress'
											) }
											value={ siteInfo.wp_directory }
										/>
									</tbody>
								</Table>
							</CardBody>
						</Card>
						<div
							style={ {
								display: 'flex',
								gap: '1rem',
								marginTop: '1rem',
							} }
						>
							<Button
								variant="primary"
								onClick={ copyToClipboard }
							>
								{ __( 'Copy to Clipboard', 'boilpress' ) }
							</Button>
							<Button
								variant="link"
								onClick={ () =>
									( window.location.href = `${ baseURL }/wp-admin/site-health.php?tab=debug` )
								}
							>
								{ __( 'More Information', 'boilpress' ) }
							</Button>
							<Button
								variant="link"
								onClick={ () => setIsModalOpen( true ) }
								style={ { marginLeft: 'auto' } }
							>
								{ __( 'Clear Cache', 'boilpress' ) }
							</Button>
						</div>
						{ isModalOpen && (
							<Modal
								title={ __( 'Clear Cache', 'boilpress' ) }
								onRequestClose={ () => setIsModalOpen( false ) }
							>
								<div style={ { marginBottom: '16px' } }>
									<p>
										Clearing the cache will delete all
										temporary data, including object cache,
										transients, and OPcache. This may cause:
									</p>
									<ul
										style={ {
											paddingLeft: '20px',
											marginBottom: '16px',
										} }
									>
										<li>
											Temporary slowdowns as data is
											recalculated.
										</li>
										<li>
											Increased load on the server as
											cache rebuilds.
										</li>
										<li>
											Potential impact on active users
											currently on the site.
										</li>
									</ul>
									<p>
										<strong>
											Only proceed if necessary,
										</strong>{ ' ' }
										as this action cannot be undone and may
										briefly affect site performance.
									</p>
								</div>
								<strong>
									Are you sure you want to continue?
								</strong>
								<div
									style={ {
										marginTop: '16px',
										display: 'flex',
										gap: '8px',
									} }
								>
									<Button
										variant="primary"
										onClick={ clearCache }
									>
										{ clearingCache ? (
											<Spinner />
										) : (
											__( 'Clear Cache', 'boilpress' )
										) }
									</Button>
									<Button
										variant="secondary"
										onClick={ () =>
											setIsModalOpen( false )
										}
									>
										{ __( 'Cancel', 'boilpress' ) }
									</Button>
								</div>
							</Modal>
						) }
					</>
				)
			) }
		</div>
	);
};

export default SiteInformation;
